var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import * as React from "react";
import { bind } from "decko";
import { GenericInputElement } from "./genericinputfield";
import { FlexContainer } from "../grid/flexcontainer";
import { Grid } from "../grid/grid";
import { GridItem } from "../grid/griditem";
import { formatNumber, isInteger } from "../helper/number-format";
import { isNullOrUndefined } from "../better/utils";
import { css, cx } from "emotion";
import { getThemeColorVar } from "../helper/color";
import classNames from "classnames";
import { suffixClass, suffixSmallClass } from "./numberfield";
const readOnlyOverlayWrapperClass = css({
    ":focus-within": {
        ".revo-input-number-overlay-wrapper": {
            display: "none",
        },
    },
    ":not(:focus-within)": {
        overflow: "hidden",
        "input, input[disabled], input::placeholder, input[disabled]:not(:focus)": {
            color: "transparent",
        },
        ".revo-input-number-overlay-wrapper": {
            pointerEvents: "none",
            gridArea: "1 / 1 / auto / auto",
            alignSelf: "center",
            fontSize: "16px",
            paddingRight: "9px",
            paddingLeft: "9px",
            minWidth: 0,
            p: {
                maxWidth: "100%",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
            },
        },
    },
    ".revo-grid input": {
        height: "100%",
    },
});
const disabledClass = css({
    p: {
        color: getThemeColorVar("typo", "light"),
    },
});
export class NumericField extends GenericInputElement {
    constructor(props) {
        var _a, _b;
        super(props);
        this.validators.push(this.createMinNumberValidator(), this.createMaxNumberValidator(), this.createIsIntegerValidator());
        this.state = Object.assign(Object.assign({}, this.defaultState), { textValue: (_b = (_a = this.props.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "" });
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        var _a, _b;
        if (this.props.value !== prevProps.value) {
            this.setState({ textValue: (_b = (_a = this.props.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "" });
        }
    }
    createMinNumberValidator() {
        return (value) => {
            let isValid = true;
            if (!isNullOrUndefined(this.props.min) && !isNullOrUndefined(value)) {
                isValid = this.props.min <= value;
            }
            return {
                valid: isValid,
                validationMessage: this.translate.validation.min(isNullOrUndefined(this.props.min) ? 0 : this.props.min),
            };
        };
    }
    createMaxNumberValidator() {
        return (value) => {
            let isValid = true;
            if (!isNullOrUndefined(this.props.max) && !isNullOrUndefined(value)) {
                isValid = this.props.max >= value;
            }
            return {
                valid: isValid,
                validationMessage: this.translate.validation.max(isNullOrUndefined(this.props.max) ? 0 : this.props.max),
            };
        };
    }
    createIsIntegerValidator() {
        return (value) => {
            let isValid = true;
            if (this.props.integer && !isNullOrUndefined(value)) {
                isValid = isInteger(value);
            }
            return {
                valid: isValid,
                validationMessage: this.translate.validation.integer,
            };
        };
    }
    componentDidMount() {
        this.inputElRef && this.props.javaScriptAutoFocus && this.inputElRef.focus();
    }
    normalizeValue(value) {
        return value.replace(/[,.]/, ".");
    }
    handleChange(event) {
        this.setState({ isPristine: false });
        const parsedValue = event.target.value.length > 0
            ? parseFloat(this.normalizeValue(event.target.value))
            : undefined;
        this.setState({ textValue: event.target.value });
        if (this.props.onValueChange &&
            (parsedValue === undefined || isFinite(parsedValue))) {
            this.props.onValueChange(parsedValue, this.validate(parsedValue).length === 0);
        }
    }
    handleKeyDown(event) {
        if (event.key === "Enter") {
            const value = (this.inputElRef && this.inputElRef.value) || "";
            const parsedValue = parseFloat(this.normalizeValue(value));
            this.props.onValueCommit &&
                isFinite(parsedValue) &&
                this.props.onValueCommit(parsedValue, this.validate(parsedValue).length === 0);
        }
        this.props.onKeyDown && this.props.onKeyDown(event);
    }
    handleBlur(event) {
        var _a, _b, _c, _d;
        const value = (this.inputElRef && this.inputElRef.value) || "";
        const parsedValue = parseFloat(this.normalizeValue(value));
        if (isFinite(parsedValue)) {
            let isValid = this.validate(parsedValue).length === 0;
            isValid &&
                this.setState({ textValue: (_b = (_a = this.props.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "" });
            this.props.onValueCommit && this.props.onValueCommit(parsedValue, isValid);
            this.props.onBlur && this.props.onBlur(event);
        }
        else {
            this.setState({ textValue: (_d = (_c = this.props.value) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : "" });
        }
    }
    inputField() {
        const overlayClasses = [];
        this.props.disabled && overlayClasses.push(disabledClass);
        const valueString = isNullOrUndefined(this.state.textValue)
            ? ""
            : this.state.textValue
                .toString()
                .replace(".", this.translate.primitive.decimalSeparator);
        return (React.createElement("div", { className: cx(readOnlyOverlayWrapperClass) },
            React.createElement(Grid, { columns: 1, height: [100, "%"] },
                React.createElement(GridItem, { colStart: 1, rowStart: 1 },
                    React.createElement("input", { tabIndex: this.props.disabled ? -1 : this.props.tabIndex, id: this.props.id, ref: (el) => (this.inputElRef = el), type: "text", autoFocus: this.props.autoFocus, value: valueString, placeholder: this.props.hint, required: this.props.required, disabled: this.props.disabled, onBlur: this.handleBlur, onChange: this.handleChange, onFocus: this.defaultHandleFocus, onKeyDown: this.handleKeyDown, lang: this.props.lang, autoComplete: this.props.autoComplete })),
                React.createElement("div", { className: classNames("revo-input-number-overlay-wrapper", cx(overlayClasses)) },
                    React.createElement("p", null, this.props.formattingEnabled
                        ? formatNumber(this.props.value, this.props.precision, undefined)
                        : valueString)))));
    }
    renderInner() {
        if (this.props.suffix) {
            const suffixClasses = [suffixClass];
            this.props.size === "small" && suffixClasses.push(suffixSmallClass);
            return (React.createElement(Grid, { columnSpec: "auto minmax(0, min-content)" },
                this.inputField(),
                this.props.suffix && (React.createElement(FlexContainer, { direction: "row", canShrink: false },
                    React.createElement("div", { className: cx(suffixClasses) }, this.props.suffix)))));
        }
        else {
            return this.inputField();
        }
    }
}
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], NumericField.prototype, "normalizeValue", null);
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], NumericField.prototype, "handleChange", null);
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], NumericField.prototype, "handleKeyDown", null);
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], NumericField.prototype, "handleBlur", null);
export default NumericField;
//# sourceMappingURL=numericfield.js.map